"use client";

import * as stylex from "@stylexjs/stylex";
import { colors, fonts, fontSizes } from "@softmax/design-system/tokens.stylex";
import Markdown, { type Components } from "react-markdown";
import rehypeSlug from "rehype-slug";
import remarkGfm from "remark-gfm";
import { MarkdownContext, type MarkdownOptions } from "./markdown-context";
import { markdownHeading } from "./markdown-heading";
import { markdownCodeElements } from "./markdown-code";
import { markdownElements } from "./markdown-elements";
import { markdownTableElements } from "./markdown-table";
import { markdownMediaElements } from "./markdown-media";
import { rewriteWikilinks } from "./rewrite-wikilinks";

export type CommunityMarkdownProps = MarkdownOptions &
  Readonly<{
    body: string;
    headingSlugs?: boolean | undefined;
  }>;

const components: Components = {
  ...markdownElements,
  ...markdownCodeElements,
  ...markdownTableElements,
  ...markdownMediaElements,
  h1: markdownHeading(1),
  h2: markdownHeading(2),
  h3: markdownHeading(3),
  h4: markdownHeading(4),
  h5: markdownHeading(5),
  h6: markdownHeading(6),
};

export function CommunityMarkdown({
  body,
  wikiPageHref,
  existingPageSlugs,
  demoteHeadings = false,
  headingSlugs = false,
  variant = "document",
  renderLink,
}: CommunityMarkdownProps) {
  return (
    <MarkdownContext
      value={{
        wikiPageHref,
        existingPageSlugs,
        demoteHeadings,
        variant,
        renderLink,
      }}
    >
      <div
        {...stylex.props(
          styles.prose,
          variant === "message" && styles.messageProse,
          variant === "article" && styles.articleProse,
        )}
      >
        <Markdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={headingSlugs ? [rehypeSlug] : undefined}
          components={components}
        >
          {wikiPageHref ? rewriteWikilinks(body) : body}
        </Markdown>
      </div>
    </MarkdownContext>
  );
}

const styles = stylex.create({
  prose: {
    color: colors.foreground,
    fontFamily: fonts.sans,
    fontSize: fontSizes.body,
    lineHeight: 1.65,
    overflowWrap: "anywhere",
    minWidth: 0,
  },
  messageProse: { fontSize: fontSizes.body, lineHeight: "normal" },
  articleProse: { color: colors.editorialBody, fontSize: 17, lineHeight: 1.65 },
});
